import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useApiClient } from '../utils/apiClient';
import { auth } from '../utils/auth';
import { API_URL } from '../constants';
import axios from 'axios';
import { authCookies } from '../utils/cookies';
import NameChangeModal from '../components/NameChangeModal';

export const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const api = useApiClient();
    const apiRef = useRef(api);
    apiRef.current = api;
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showNameChangeModal, setShowNameChangeModal] = useState(false);

    const exchangeCodeForToken = async (code) => {
        try {
            const response = await axios.get(`${API_URL}/auth/token?code=${code}`);
            if (response.data.error) throw new Error(response.data.error);
            if (response.data.id_token) {
                auth.clearTokens();
                authCookies.setIdToken(response.data.id_token);
            }
            if (response.data.access_token) authCookies.setAccessToken(response.data.access_token);
            if (response.data.refresh_token) authCookies.setRefreshToken(response.data.refresh_token);
        } catch (error) {
            auth.clearTokens();
            throw error;
        }
    };

    const validateToken = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = await auth.getValidToken();
            if (token) {
                const decodedToken = jwtDecode(token);
                const currentUser = {
                    sub: decodedToken.sub,
                    email: decodedToken.email,
                    email_verified: decodedToken.email_verified,
                    name: decodedToken.name,
                };

                try {
                    const profileResponse = await apiRef.current.get('/user_profile');
                    
                    if (profileResponse.user_id && profileResponse.user_id !== decodedToken.sub) {
                        console.warn('Token user_id mismatch detected. User was likely deleted and recreated. Clearing token and forcing logout.');
                        auth.clearTokens();
                        setIsAuthenticated(false);
                        setUser(null);
                        navigate('/login');
                        return;
                    }
                    
                    if (profileResponse.role) {
                        currentUser.role = profileResponse.role;
                        if (profileResponse.active_role) {
                            currentUser.activeRole = profileResponse.active_role;
                        } else {
                            const roles = profileResponse.role.split(',');
                            currentUser.activeRole = roles[0].trim();
                        }
                    }
                    if (profileResponse.user_name) {
                        currentUser.name = profileResponse.user_name;
                    }
                    
                    setUser(currentUser);
                    setIsAuthenticated(true);
                } catch (profileError) {
                    if (profileError.response && profileError.response.status === 404) {
                        console.warn('User profile not found during token validation. This might be a new user or the profile is still being created.');
                        if (decodedToken.email) {
                            currentUser.role = 'Provider';
                            currentUser.activeRole = 'Provider';
                            setUser(currentUser);
                            setIsAuthenticated(true);
                        } else {
                            console.error('No email in token, clearing tokens');
                            auth.clearTokens();
                            setIsAuthenticated(false);
                            setUser(null);
                        }
                    } else {
                        console.error('Profile fetch error:', profileError);
                        currentUser.role = 'Provider';
                        currentUser.activeRole = 'Provider';
                        setUser(currentUser);
                        setIsAuthenticated(true);
                    }
                }
            } else {
                setIsAuthenticated(false);
                setUser(null);
            }
        } catch (error) {
            console.error('Token validation error:', error);
            setIsAuthenticated(false);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        const checkInitialAuth = async () => {
            try {
                const token = await auth.getValidToken();
                if (token) {
                    await validateToken();
                } else {
                    setIsLoading(false);
                }
            } catch (error) {
                console.error('Initial auth check failed:', error);
                setIsLoading(false);
            }
        };
        
        checkInitialAuth();
    }, []);

    useEffect(() => {
        if (user) {
            if (isDefaultGoogleHandler(user.name)) {
                setShowNameChangeModal(true);
            }
        }
    }, [user]);

    const logout = useCallback(() => {
        auth.clearTokens();
        setUser(null);
        setIsAuthenticated(false);
        console.log('[AuthContext] logout called, navigating to /login');
        navigate('/login');
    }, [navigate]);

    const refreshAuth = async () => {
        const success = await auth.refreshToken();
        if (success) {
            await validateToken();
        } else {
            logout();
        }
        return success;
    };

    const switchRole = async (newRole) => {
        try {
            const response = await apiRef.current.post('/user_profile', {
                action: 'switch_role',
                new_role: newRole
            });


            setUser(prev => {
                const updatedUser = { ...prev, activeRole: newRole };
                console.log('Updated user state:', updatedUser);
                return updatedUser;
            });
            return true;
        } catch (error) {
            return false;
        }
    };

    const isDefaultGoogleHandler = (name) => {
        if (!name) return false;
        
        const trimmedName = name.trim();
        
        const isGoogleHandler = /^GOOGLE_\d+$/i.test(trimmedName);
        
        const isCognitoUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmedName);
        
        return isGoogleHandler || isCognitoUUID;
    };

    const updateUserName = (newName) => {
        setUser(prev => {
            const updatedUser = { ...prev, name: newName };
            console.log('Updated user name:', updatedUser);
            return updatedUser;
        });
    };

    const value = {
        user,
        isAuthenticated,
        isLoading,
        logout,
        refreshAuth,
        switchRole,
        updateUserName,
        api,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            <NameChangeModal 
                isOpen={showNameChangeModal} 
                onClose={() => setShowNameChangeModal(false)} 
            />
        </AuthContext.Provider>
    );
}; 